import WboTransportProvider
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-transport-provider.interface';
import {Socket} from 'socket.io-client';
import {
    WboAnnotationDocumentMetadata,
    WboDelta,
    WboRequestMessage,
    WboServerResponse
} from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import WboConnectionStatusObserver
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-connection-status-observer.interface';

/**
 * A transport provider that uses Socket.IO to communicate with the server.
 */
export default class WboSocketIoTransportProvider implements WboTransportProvider {
    private observers: WboConnectionStatusObserver[];
    private ackTimeout: number;

    constructor(private readonly socket: Socket, private readonly boardName: string,
                transportOptions: WboSocketIoTransportProviderOptions) {
        this.observers = [];
        this.ackTimeout = transportOptions.ackTimeout ?? 5000;
        this.initialize();
    }

    addConnectionStatusObserver(observer: WboConnectionStatusObserver) {
        this.observers.push(observer);
    }

    isConnected(): boolean {
        return this.socket?.connected;
    }

    /**
     * Registers the required event listeners for the transport provider.
     */
    initialize() {
        this.socket.on('connect', () => {
            this.observers.forEach(o => o.onConnect());
        });
        this.socket.on('disconnect', () => {
            this.observers.forEach(o => o.onDisconnect());
        });
        this.socket.on('reconnect', () => {
            this.observers.forEach(o => o.onReconnect());
        });

        // When the server acknowledges that the client has joined a board, notify the observers, if the board name
        // matches.
        this.socket.on('board_joined', msg => {
            if (msg.boardName === this.boardName) {
                this.observers.forEach(o => o.onSubscribe());
            }
        });
    }

    /**
     * Subscribes to a board on the server.
     */
    async subscribe(boardName: string, meta: WboAnnotationDocumentMetadata): Promise<void> {
        // 1. Create the board
        await this.socket.timeout(this.ackTimeout).emitWithAck('createBoard', boardName);

        // 2. Subscribe to the board and wait for the ACK.
        this.socket.timeout(this.ackTimeout).emit('getboard', boardName, 2, meta.width, meta.height,
            !!meta.isTransparency);
    }

    /**
     * Sends a Delta to the server. Requires an ACK from the server. Callers are responsible for taking action upon the
     * success or failure of the send operation. Retry logic must be handled by callers.
     *
     */
    async sendDeltaWithAck(delta: WboDelta): Promise<WboServerResponse | undefined> {
        if (!this.socket.connected) {
            return Promise.resolve(undefined);
        }
        try {
            const ackResponse = await this.socket.timeout(this.ackTimeout).emitWithAck(delta.opType, delta.msg);
            return Promise.resolve({
                originalMessage: delta, response: ackResponse
            });
        } catch (error) {
            return Promise.reject({
                originalMessage: delta, response: error
            });
        }
    }

    /**
     * Sends a delta to the server without waiting for an ACK.
     */
    sendDelta(message: WboDelta): void {
        if (this.socket.connected) {
            this.socket.emit(message.opType, message.msg);
        }
    }

    /**
     * Sends a special kind of message in a request / response fashion.
     */
    async sendRequest(req: WboRequestMessage): Promise<any> {
        if (!this.socket.connected) {
            return;
        }
        try {
            const response = await this.socket.timeout(this.ackTimeout).emitWithAck(req.requestType, req.request);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    /**
     * Allows callers to register a handler function to run when a message arrives, or it's fetched from the server.
     * @param handler
     */
    onMessage(handler: (delta: WboDelta) => void) {
        this.socket.on('broadcast', msg => handler(this.mapIncomingMsgToWboDelta('broadcast', msg)));
        this.socket.on('removeAllByUser', msg => handler(this.mapIncomingMsgToWboDelta('removeAllByUser', msg)));
    }

    close() {
        throw new Error('Method not implemented.');
    }

    /**
     * Just a helper method to map incoming messages to WBO Deltas.
     */
    private mapIncomingMsgToWboDelta(opType: string, msg: any): WboDelta {
        if (opType === 'broadcast') {
            return {
                msg: {
                    board: msg.boardName,
                    clientId: msg.clientId,
                    data: msg,
                    operationId: msg.operationId,
                    userName: msg.userName,
                },
                opType,
                v: msg.version
            };
        } else if (opType === 'removeAllByUser') {
            return {
                msg: {
                    board: msg.boardName,
                    clientId: msg.clientId,
                    elementsToDelete: msg.elementsToDelete,
                    operationId: msg.operationId,
                    userName: msg.userName,
                },
                opType,
                v: msg.version
            };
        }
    }
}

export interface WboSocketIoTransportProviderOptions {
    /**
     * How much time (in milliseconds) should the client wait for an ACK for each emitted message.
     */
    ackTimeout: number;


    /**
     * How may times should the client retry the emission of an operation before giving up.
     */
    emitRetries: number;

}